import React from "react";
import { Link, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Toaster, toast } from "sonner";

export default function AgenziaLayout() {
  const { user, agency, loading, logout, impersonating, stopImpersonating } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  if (loading) return <div className="p-10 text-center text-[#5C5657]">Caricamento…</div>;
  if (!user || localStorage.getItem("rxcasa_role") !== "agency") return <Navigate to="/area-agenzie/login" replace />;
  // Forza cambio password al primo accesso: blocca ogni pagina fino a scelta nuova password
  // (bypassato in modalità "Accedi come" — sessione temporanea, non lascia traccia sul login dell'agente)
  if (user?.must_change_password && !impersonating && !loc.pathname.startsWith("/area-agenzie/primo-accesso")) {
    return <Navigate to="/area-agenzie/primo-accesso" replace />;
  }

  const links = [
    { to: "/area-agenzie", label: "Panoramica", exact: true },
    { to: "/area-agenzie/immobili", label: "I miei immobili" },
    { to: "/area-agenzie/richieste", label: "Le mie richieste" },
    { to: "/area-agenzie/documentazione", label: "Richieste documentazione" },
    { to: "/area-agenzie/profilo", label: "Profilo agenzia" },
  ];
  const active = (l) => l.exact ? loc.pathname === l.to : loc.pathname.startsWith(l.to);
  const onLogout = async () => { await logout(); nav("/area-agenzie/login"); };
  const onStopImpersonate = async () => {
    const r = await stopImpersonating();
    toast.success(`Sessione di impersonazione terminata`);
    // replace: torna sempre in admin senza lasciare traccia della sessione imp nella history
    nav(r.redirect || "/admin/agenzie", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F3F1] flex flex-col" data-testid="agenzia-layout">
      <Toaster position="top-right" richColors />
      {impersonating && (
        <div className="bg-[#7D2E3F] text-white px-6 py-2 flex items-center justify-between gap-4 text-xs" data-testid="impersonation-banner">
          <div>
            <span className="label-uppercase mr-3 opacity-70">Modalità QA — Admin</span>
            Stai visualizzando come <strong>{impersonating.agent_name || user?.name}</strong>
            {impersonating.agent_email ? <span className="opacity-70"> ({impersonating.agent_email})</span> : null}
            <span className="opacity-70"> · agenzia </span>
            <strong>{impersonating.agency_name || agency?.name}</strong>
            <span className="opacity-70"> · sessione temporanea (2h), nessun impatto sulle credenziali</span>
          </div>
          <button onClick={onStopImpersonate} data-testid="impersonation-exit"
                  className="label-uppercase px-3 py-1 border border-white/60 hover:bg-white hover:text-[#7D2E3F]">
            Esci dalla visualizzazione
          </button>
        </div>
      )}
      <div className="flex-1 flex">
        <aside className="w-64 border-r border-[#E6E4E0] bg-white flex flex-col">
          <div className="p-6 border-b border-[#E6E4E0]">
            <Link to="/area-agenzie">
              <div className="font-serif text-lg text-[#7D2E3F] tracking-[0.12em] leading-none">PRONTO ALLA VENDITA</div>
              <div className="label-uppercase mt-1 text-[10px]">Area Agenzie Partner</div>
            </Link>
          </div>
          <div className="px-6 py-4 border-b border-[#E6E4E0]">
            <div className="label-uppercase text-[#5C5657]">Agenzia</div>
            <div className="font-serif text-lg mt-1">{agency?.name}</div>
            <div className="text-xs text-[#5C5657] mt-1">{agency?.comune}{agency?.provincia ? ` · ${agency.provincia}` : ""}</div>
          </div>
          <nav className="flex-1 py-4">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`agenzia-nav-${l.label.toLowerCase().replace(/ /g,"-")}`}
                className={`block px-6 py-3 text-sm border-l-2 ${active(l) ? "border-[#7D2E3F] bg-[#F2EBEB] text-[#7D2E3F]" : "border-transparent hover:bg-[#F4F3F1]"}`}>
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="border-t border-[#E6E4E0] p-4">
            <div className="text-xs text-[#5C5657]">{user?.name}</div>
            <div className="text-xs text-[#3A3D42] mb-3 truncate">{user?.email}</div>
            {impersonating ? (
              <button data-testid="agenzia-logout" onClick={onStopImpersonate}
                      className="w-full text-xs label-uppercase border border-[#7D2E3F] text-[#7D2E3F] py-2 hover:bg-[#F2EBEB]">
                Torna al pannello Admin
              </button>
            ) : (
              <button data-testid="agenzia-logout" onClick={onLogout} className="w-full text-xs label-uppercase border border-[#E6E4E0] py-2 hover:border-[#7D2E3F] hover:text-[#7D2E3F]">Esci</button>
            )}
          </div>
        </aside>
        <main className="flex-1 overflow-x-auto">
          <div className="p-8 max-w-[1400px]"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}
